import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[4px] text-[11px] font-bold uppercase tracking-[0.06em] transition-all duration-150 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--color-hero)] text-[#1a1c00] hover:opacity-90",
        destructive:
          "bg-transparent border border-[rgba(217,48,37,0.30)] text-[#D93025] hover:bg-[rgba(217,48,37,0.08)]",
        outline:
          "bg-transparent border border-[rgba(255,255,255,0.10)] text-[#888] hover:bg-[rgba(255,255,255,0.06)]",
        secondary:
          "bg-transparent border border-[rgba(255,255,255,0.10)] text-[#888] hover:bg-[rgba(255,255,255,0.06)]",
        ghost:
          "bg-transparent text-[#666] hover:bg-[rgba(255,255,255,0.06)] hover:text-[#aaa]",
        link:
          "text-[var(--color-hero)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-8 px-4",
        sm: "h-7 px-3",
        lg: "h-9 px-6",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
